import { Spreadsheet } from '@progress/kendo-react-spreadsheet';
import "@progress/kendo-theme-default/dist/all.css";
import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { Box } from '@mui/material';
import { toast } from 'react-toastify';
import {
  fetchFilteredHarvestDetails,
  postUpdateHarvestDetail,
  selectEditableColumns,
  selectColumnMetadata,
  selectUpdating
} from '../../slices/harvestdetail.slice';
import CustomButton from '../../components/Button';
import styles from './HarvestDetail.module.scss';
import { ConfirmationDialog } from "../../components/ConfirmationDialog/ConfirmationDialog";
import { CellUpdate } from '../../types/revenueDetail';

interface HarvestDetailSpreadsheetProps {
  data: any;
  selectedYear: number;
  selectedLandlords: any[];
  selectedCrops: any[]; 
  selectedFields: any[];
  loading: boolean;
  onSaveSuccess?: () => void;
}

export const HarvestDetailSpreadsheetComponent: React.FC<HarvestDetailSpreadsheetProps> = ({
  data,
  selectedYear,
  selectedLandlords,
  selectedCrops,
  selectedFields,
  loading,
  onSaveSuccess
}) => {
    const [spreadSheetKey, setSpreadSheetKey] = useState<string | undefined>(new Date().toISOString());
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const sheetRef = useRef<any>(null);
    const isUpdatedHarvestDetail = useRef(false);
    const [resultArray, setResultArray] = useState<CellUpdate[]>([]);
    const originalCellValue = useRef<Map<string, any>>(new Map());
    const dispatch = useAppDispatch();
    
    // Get editable columns from Redux store
    const editableColumns = useAppSelector(selectEditableColumns);
    const getColumnMetadata = useAppSelector(selectColumnMetadata);
    const updating = useAppSelector(selectUpdating);

    // Dynamic field name mapping based on editable columns from API
    const getFieldNameFromColumnIndex = (columnIndex: number): string => {
        const columnMetadata = editableColumns.find(col => col.columnIndex === columnIndex);
        return columnMetadata?.fieldName || '';
    };

    // Get column index from field name
    const getColumnIndexFromFieldName = (fieldName: string): number => {
        const columnMetadata = editableColumns.find(col => col.fieldName === fieldName);
        return columnMetadata?.columnIndex || -1;
    };

    const isColumnEditable = (columnIndex: number): boolean => {
        return editableColumns.some(col => col.columnIndex === columnIndex);
    };

    // Dynamic validation based on column metadata
    const validateCellValue = (value: any, columnIndex: number): { isValid: boolean; message?: string } => {
        const metadata = getColumnMetadata(columnIndex);
        if (!metadata) return { isValid: true };

        // Check if empty and required
        if (metadata.isRequired && (value === null || value === undefined || value === '')) {
            return { isValid: false, message: `${metadata.displayName} is required` };
        }

        // Skip validation for empty optional fields
        if (!metadata.isRequired && (value === null || value === undefined || value === '')) {
            return { isValid: true };
        }

        // Validate data type
        if (metadata.dataType === 'decimal') {
            const numValue = parseFloat(value);
            if (isNaN(numValue)) {
                return { isValid: false, message: 'Please enter a valid number' };
            }

            // Check range
            if (metadata.minValue !== null && metadata.minValue !== undefined && numValue < metadata.minValue) {
                return { isValid: false, message: `${metadata.displayName} must be at least ${metadata.minValue}` };
            }
            if (metadata.maxValue !== null && metadata.maxValue !== undefined && numValue > metadata.maxValue) {
                return { isValid: false, message: `${metadata.displayName} must be at most ${metadata.maxValue}` };
            }
        }

        return { isValid: true };
    };

    const handleCancel = () => {
        setShowCancelDialog(true);
    }

    useEffect(() => {
        setSpreadSheetKey(new Date().toISOString())
        setResultArray([]);
        originalCellValue.current.clear();
    }, [data])

    const handleSaveDialogClose = () => setShowSaveDialog(false);
    const handleCancelDialogClose = () => setShowCancelDialog(false);

    const handleSaveYes = () => {
        setShowSaveDialog(false);
        handleSave();
    };

    const handleCancelYes = () => {
        setShowCancelDialog(false);
        // Refresh data with current filters instead of calling the old API
        const requestData = {
            year: selectedYear,
            landlordIds: selectedLandlords.map((value) => value.id),
            ...(selectedCrops.length > 0 && { cropIds: selectedCrops.map((value) => value.id) }),
            ...(selectedFields.length > 0 && { fieldIds: selectedFields.map((value) => value.id) }),
        };
        dispatch(fetchFilteredHarvestDetails(requestData));
    };

    const onClickSave = () => {
        setShowSaveDialog(true);
    }

    const handleSave = async () => {
        if (resultArray.length === 0) {
            toast.warn("No changes are made!");
            return;
        }

        const requestData = {
            cellUpdates: resultArray
        };

        const result = await dispatch(postUpdateHarvestDetail(requestData));
        if (result?.meta?.requestStatus === "fulfilled") {
            toast.success("Harvest Detail updated successfully.");
            setResultArray([]);
            onSaveSuccess?.();
        }
        else if (result?.meta?.requestStatus === "rejected") {
            toast.error("Unable to update Harvest Detail Data");
            setResultArray([]);
            setSpreadSheetKey(new Date().toISOString());
        }
        else {
            toast.error("Something went wrong.");
            setSpreadSheetKey(new Date().toISOString());
        }
    }

    const updateRecordInExcel = (rows: any[] | undefined, value: any, ri: number, ci: number, fieldName: string): CellUpdate | null => {
        if (!rows || !fieldName) return null;

        let cellData: CellUpdate | null = null;

        rows.forEach(row => {
            const rowIndex = row.index;
            const cells = row.cells;

            if (ri === rowIndex) {
                cells.forEach((item: any) => {
                    const columnIndex = item.index;
                    
                    if (columnIndex === ci && item.enable === true) {
                        item.value = value;
                        
                        if (item.harvestDetailId) {
                            cellData = {
                                harvestDetailId: item.harvestDetailId,
                                fieldName: fieldName,
                                value: value?.toString() || ''
                            };
                            
                            console.log('Cell data extracted:', {
                                harvestDetailId: item.harvestDetailId,
                                fieldName: fieldName,
                                value: value,
                                cellIdentifier: item.identifier
                            });
                        }
                    }
                });
            }
        });

        return cellData;
    }

    const calculatePartnerShares = (rowIndex: number, updatedField: string, newValue: any): void => {
        const sheet = data?.sheets?.[0];
        if (!sheet?.rows?.[rowIndex]) return;

        const row = sheet.rows[rowIndex];
        const cells = row.cells || [];
        
        // Only recalculate if TotalBushels changed
        if (updatedField.toLowerCase() !== 'totalbushels') return;

        const totalBushels = parseFloat(newValue) || 0;
        
        // Get current values from the row
        const getCurrentCellValue = (fieldName: string): number => {
            const colIndex = getColumnIndexFromFieldName(fieldName);
            if (colIndex === -1) return 0;
            
            const cell = cells.find((c: any) => c.index === colIndex);
            return parseFloat(cell?.value) || 0;
        };

        // Update partner shares
        cells.forEach((cell: any) => {
            if (cell.fieldName && cell.fieldName.includes('Share') && 
                cell.fieldName.includes('Amount')) {
                
                const percentageFieldName = cell.fieldName.replace('Amount', 'Percentage');
                const percentageCell = cells.find((c: any) => c.fieldName === percentageFieldName);
                
                if (percentageCell) {
                    const percentage = parseFloat(percentageCell.value) || 0;
                    const contractPrice = getCurrentCellValue('ContractPrice') || 0;
                    const newShareAmount = (totalBushels * contractPrice * percentage / 100);
                    
                    cell.value = newShareAmount.toFixed(2);
                    
                    // Update the spreadsheet UI
                    try {
                        const workbook = sheetRef.current?.workbook;
                        const activeSheet = workbook?.activeSheet();
                        if (activeSheet) {
                            const uiCell = activeSheet.range(rowIndex, cell.index);
                            uiCell.value(newShareAmount.toFixed(2));
                        }
                    } catch (error) {
                        console.error('Error updating calculated cell:', error);
                    }
                }
            }
        });
    };

    const handleSelectedCellValue = (e: any) => {
        const range = e.range;
        const row = range._ref.row;
        const col = range._ref.col;
        const cellKey = `${row}-${col}`;
        const workbook = sheetRef.current?.workbook;
        const sheet = workbook?.activeSheet();
        if (!sheet) return;

        const cell = sheet.range(row, col);
        const currentValue = cell.value() ?? 0;

        if (!originalCellValue.current.has(cellKey)) {
            originalCellValue.current.set(cellKey, currentValue);
        }
    };

    const handleCellValueChange = (e: any) => {
        if (isUpdatedHarvestDetail.current || !sheetRef.current) return;

        const range = e.range;
        const row = range._ref.topLeft.row;
        const col = range._ref.topLeft.col;
        const cellKey = `${row}-${col}`;
        let value = range.value();
        const data = e.sender.options.sheets;
        const fieldName = getFieldNameFromColumnIndex(col);

        console.log('Cell change detected:', { row, col, value, fieldName });

        // Check if column is editable using dynamic metadata
        if (!isColumnEditable(col)) {
            console.log('Column is not editable:', col);
            return;
        }

        if (!fieldName) {
            console.log('No field name found for column:', col);
            return;
        }

        const originalValue = originalCellValue.current.get(cellKey) ?? 0;

        // Use dynamic validation
        const validation = validateCellValue(value, col);
        if (!validation.isValid) {
            toast.error(validation.message || `Invalid value for ${fieldName}`);
            value = originalValue;
        } else {
            originalCellValue.current.set(cellKey, value);
        }

        isUpdatedHarvestDetail.current = true;

        try {
            const workbook = sheetRef.current.workbook;
            const sheet = workbook.activeSheet();
            if (sheet) {
                const cell = sheet.range(row, col);
                cell.value(value);
            }

            if (data !== undefined) {
                if (value !== originalValue) {
                    const cellData = updateRecordInExcel(data[0].rows, value, row, col, fieldName);

                    if (!cellData || cellData.harvestDetailId === 0) {
                        console.error('Missing required harvest detail ID:', cellData);
                        toast.error('Unable to identify harvest detail for this cell');
                        return;
                    }

                    console.log('✅ Cell data after update:', cellData);

                    // Calculate partner shares if this field affects them
                    const fieldsAffectingShares = ['TotalBushels'];
                    if (fieldsAffectingShares.includes(fieldName)) {
                        calculatePartnerShares(row, fieldName, value);
                    }

                    setResultArray((prevData) => {
                        // Remove any existing update for the same harvestDetailId and fieldName
                        const filteredData = prevData.filter(item =>
                            !(item.harvestDetailId === cellData.harvestDetailId && 
                              item.fieldName === cellData.fieldName)
                        );

                        filteredData.push({
                            harvestDetailId: cellData.harvestDetailId,
                            fieldName: cellData.fieldName,
                            value: cellData.value
                        });

                        console.log('Updated result array:', filteredData);
                        return filteredData;
                    });
                }
            }
        }
        catch (error) {
            console.error('Error in handleCellValueChange:', error);
            toast.error('Error updating cell value');
        }
        finally {
            isUpdatedHarvestDetail.current = false;
        }
    };

    return (
        <div>
            <ConfirmationDialog
                open={showSaveDialog}
                title="Save"
                description="Do you want to save the changes to harvest data? Partner share amounts will be recalculated."
                handleNo={handleSaveDialogClose}
                handleYes={handleSaveYes}
            />

            <ConfirmationDialog
                open={showCancelDialog}
                title="Warning"
                description="Are you sure you want to cancel? Any unsaved changes will be lost."
                handleNo={handleCancelDialogClose}
                handleYes={handleCancelYes}
            />
{loading ? (
    <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        Loading data...
    </Box>
) : Object.keys(data).length === 0 ? (
    <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        Loading data...
    </Box>
) : (
    <>
        <Spreadsheet
            className={styles.spreadsheet}
            key={spreadSheetKey}
            ref={sheetRef}
            toolbar={false}
            style={{
                width: "100%",
                height: "80vh",
            }}
            onChange={handleCellValueChange}
            onSelect={handleSelectedCellValue}
            defaultProps={data}  // Changed back to defaultProps like Service Agreement
        />
        <Box
            sx={{ display: "flex", justifyContent: "center", gap: "1rem" }}
        >
            <button 
                onClick={handleCancel} 
                className={styles.cancelButton}
                disabled={updating}
            >
                Cancel
            </button>
            <CustomButton
                onClick={onClickSave}
                disabled={!resultArray.length || updating}
                sx={{ width: "110px" }}
            >
                {updating ? "Saving..." : "Save"}
            </CustomButton>
        </Box>
    </>
)}
        </div>
    );
}